import { useCallback, useEffect, useRef, useState } from 'react';
import {
  NEEDS_ADDRESS_MESSAGE,
  SEARCH_BUSY_MESSAGE,
  SEARCH_FAILED_MESSAGE,
  SEARCH_NEEDS_DOORDASH_MESSAGE,
  SEARCH_THROTTLED_MESSAGE,
} from './discoveryMessages';
import {
  PROVIDER_READ_WAIT_BUDGET_MS,
  needsDoordashConnect,
  providerReadRetryAfterSeconds,
  providerReadRetryWaitMs,
} from '@/utils/providerReadErrors';
import { useAuth } from '@/contexts';
import { authorizedFetch } from '@/utils/apiClient';
import { normalizeRestaurantHours } from '@/utils/restaurantHours';
import { coordsToTimeZone, wallClockToIso } from '@/utils/timezoneUtils';
import {
  PLATFORM_CAPABILITY_DEFAULTS,
  normalizeProviderCapabilities,
} from '@/domains/restaurants/providers/providerCapabilities';
import {
  readPhantomRestaurantCache,
  writePhantomRestaurantCache,
} from './phantomRestaurantSnapshots';
import { filterRowsByQuery, searchQueryKey } from './restaurantSearchMatch';

const requestCache = new Map();
const goToRequestCache = new Map();

// Longest the grid will hold first paint waiting on go-to resolution. Warm
// resolutions return in ~150ms; a cold area resolves in a few seconds now
// that the fan-out is parallel; anything slower falls back to splicing in.
const GO_TO_HOLD_MS = 6000;

// A fulfillment change often lands as two renders a tick apart: the address
// pick, then a second correction from the bar (a deferred onChange). The
// organic fetch waits this long before dispatching and lets a second render
// that changes the search identity cancel the first — one provider round trip
// per switch instead of two. Cache reads, row clearing and the loading flag
// stay synchronous; only the network call is deferred.
const FETCH_COALESCE_MS = 100;

// A tab coming back into view refreshes its results only when they are at
// least this old. Hidden tabs never re-search (see the organic effect), and a
// DoorDash nearby list costs provider calls (5 on the shared account, 1 on a
// coach's own, whose search tool locks after ~60 in 9 minutes), so a quick
// tab switch must not buy a new one.
const RETURN_REFRESH_STALE_MS = 15 * 60 * 1000;

const isDocumentHidden = () => typeof document !== 'undefined' && document.hidden === true;

/**
 * A failed organic search, for the coach: `{ message, retryAfterSeconds }`.
 * A rate limit — the provider's (DoorDash limits the whole shared account) or
 * our own API's per-user quota — names a window to wait out; anything else is
 * generic. The grid used to print the raw text: Phantom's agent-worded
 * "PROVIDER_RATE_LIMITED: … [retry_after_seconds=284]", or "MCP error -32602:
 * Input validation error…" (2026-10-07).
 */
export const describeSearchFailure = (error) => {
  if (needsDoordashConnect(error)) return { message: SEARCH_NEEDS_DOORDASH_MESSAGE, retryAfterSeconds: null };
  const providerSeconds = providerReadRetryAfterSeconds(error);
  if (providerSeconds != null) return { message: SEARCH_BUSY_MESSAGE, retryAfterSeconds: providerSeconds };
  if (error?.status === 429 || error?.payload?.code === 'RATE_LIMITED') {
    const seconds = Number(error?.payload?.retryAfterSeconds);
    return { message: SEARCH_THROTTLED_MESSAGE, retryAfterSeconds: Number.isFinite(seconds) && seconds > 0 ? seconds : 60 };
  }
  return { message: SEARCH_FAILED_MESSAGE, retryAfterSeconds: null };
};

// The provider's capability declaration from the last search envelope.
// Module-level: one backend serves the whole session, so the latest envelope
// speaks for cache-served renders too; defaults until a first response.
let latestCapabilities = PLATFORM_CAPABILITY_DEFAULTS;

// Exported for the assistant chat: it normalizes the same discovery-shaped
// rows (delivered over the assistant SSE stream) into the shared snapshot
// cache so chat-opened restaurant pages match discovery-opened ones.
const phantomPriceRange = (restaurant) => {
  const text = typeof (restaurant.priceRange ?? restaurant.price_range) === 'string'
    ? String(restaurant.priceRange ?? restaurant.price_range).trim()
    : '';
  if (/^\${1,4}$/.test(text)) return text;
  const tier = Number(restaurant.priceTier ?? restaurant.price_tier);
  return Number.isInteger(tier) && tier >= 1 && tier <= 4 ? '$'.repeat(tier) : null;
};

// A row from a search over several price tiers: its tier is one of these
// ("$".."$$$$"), and which one the provider doesn't say. Null otherwise,
// including when the row's own tier is known (priceRange).
const phantomPriceRanges = (restaurant) => {
  if (phantomPriceRange(restaurant)) return null;
  const tiers = restaurant.priceTiers ?? restaurant.price_tiers;
  if (!Array.isArray(tiers)) return null;
  const ranges = [...new Set(tiers.map(Number))]
    .filter((tier) => Number.isInteger(tier) && tier >= 1 && tier <= 4)
    .sort((a, b) => a - b)
    .map((tier) => '$'.repeat(tier));
  return ranges.length ? ranges : null;
};

export const normalizePhantomRestaurant = (restaurant) => ({
  id: restaurant.localRestaurantId || restaurant.local_restaurant_id || restaurant.id,
  name: restaurant.name,
  posProvider: restaurant.posProvider || restaurant.pos_provider || '',
  pos_provider: restaurant.pos_provider || restaurant.posProvider || '',
  image:
    restaurant.image ||
    restaurant.image_url ||
    restaurant.imageUrl ||
    undefined,
  image_url:
    restaurant.image_url ||
    restaurant.image ||
    restaurant.imageUrl ||
    undefined,
  imageUrl:
    restaurant.imageUrl ||
    restaurant.image_url ||
    restaurant.image ||
    undefined,
  cuisine: restaurant.cuisine || restaurant.cuisine_type || '',
  cuisine_type: restaurant.cuisine_type || restaurant.cuisine || '',
  description: restaurant.description || undefined,
  address: restaurant.address || restaurant.location || '',
  rating: Number.isFinite(Number(restaurant.rating)) ? Number(restaurant.rating) : undefined,
  // Review count + delivery ETA (surfaced by the DoorDash provider; harmless
  // passthrough for other providers that don't supply them).
  reviewCount:
    Number.isFinite(Number(restaurant.reviewCount))
      ? Math.round(Number(restaurant.reviewCount))
      : Number.isFinite(Number(restaurant.review_count))
        ? Math.round(Number(restaurant.review_count))
        : undefined,
  deliveryTime: restaurant.deliveryTime || restaurant.delivery_time || undefined,
  // DoorDash's per-store pickup availability (asap / scheduled /
  // nextAsapOpenAt): the one closed-now signal a search row carries, since
  // it has no opening hours (see searchRowClosedUntil).
  pickup:
    restaurant.pickup && typeof restaurant.pickup === 'object'
      ? {
          asap: restaurant.pickup.asap ?? null,
          scheduled: restaurant.pickup.scheduled ?? null,
          nextAsapOpenAt: restaurant.pickup.nextAsapOpenAt ?? null,
        }
      : null,
  distance:
    Number.isFinite(Number(restaurant.distanceMiles)) ? `${Number(restaurant.distanceMiles).toFixed(1)} mi` : undefined,
  _distanceMeters:
    Number.isFinite(Number(restaurant.distanceMiles))
      ? Number(restaurant.distanceMiles) * 1609.344
      : null,
  phone_number: restaurant.contactPhone || restaurant.phone_number || '',
  contact_email: restaurant.contactEmail || restaurant.contact_email || '',
  deliveryFee:
    Number.isFinite(Number(restaurant.deliveryFee))
      ? Number(restaurant.deliveryFee)
      : Number.isFinite(Number(restaurant.delivery_fee))
        ? Number(restaurant.delivery_fee)
        : undefined,
  delivery_fee:
    Number.isFinite(Number(restaurant.delivery_fee))
      ? Number(restaurant.delivery_fee)
      : Number.isFinite(Number(restaurant.deliveryFee))
        ? Number(restaurant.deliveryFee)
        : undefined,
  minimum_order:
    Number.isFinite(Number(restaurant.minimum_order))
      ? Number(restaurant.minimum_order)
      : Number.isFinite(Number(restaurant.minimumOrder))
        ? Number(restaurant.minimumOrder)
        : undefined,
  minimumOrder:
    Number.isFinite(Number(restaurant.minimumOrder))
      ? Number(restaurant.minimumOrder)
      : Number.isFinite(Number(restaurant.minimum_order))
        ? Number(restaurant.minimum_order)
        : undefined,
  max_order_dollar_amount:
    Number.isFinite(Number(restaurant.max_order_dollar_amount))
      ? Number(restaurant.max_order_dollar_amount)
      : Number.isFinite(Number(restaurant.maxOrderDollarAmount))
        ? Number(restaurant.maxOrderDollarAmount)
        : undefined,
  maxOrderDollarAmount:
    Number.isFinite(Number(restaurant.maxOrderDollarAmount))
      ? Number(restaurant.maxOrderDollarAmount)
      : Number.isFinite(Number(restaurant.max_order_dollar_amount))
        ? Number(restaurant.max_order_dollar_amount)
        : undefined,
  max_order_cents:
    Number.isFinite(Number(restaurant.max_order_cents))
      ? Number(restaurant.max_order_cents)
      : Number.isFinite(Number(restaurant.maxOrderCents))
        ? Number(restaurant.maxOrderCents)
        : Number.isFinite(Number(restaurant.max_order_dollar_amount))
          ? Math.round(Number(restaurant.max_order_dollar_amount) * 100)
          : Number.isFinite(Number(restaurant.maxOrderDollarAmount))
            ? Math.round(Number(restaurant.maxOrderDollarAmount) * 100)
            : undefined,
  maxOrderCents:
    Number.isFinite(Number(restaurant.maxOrderCents))
      ? Number(restaurant.maxOrderCents)
      : Number.isFinite(Number(restaurant.max_order_cents))
        ? Number(restaurant.max_order_cents)
        : Number.isFinite(Number(restaurant.maxOrderDollarAmount))
          ? Math.round(Number(restaurant.maxOrderDollarAmount) * 100)
          : Number.isFinite(Number(restaurant.max_order_dollar_amount))
            ? Math.round(Number(restaurant.max_order_dollar_amount) * 100)
            : undefined,
  max_item_quantity:
    Number.isFinite(Number(restaurant.max_item_quantity))
      ? Math.round(Number(restaurant.max_item_quantity))
      : Number.isFinite(Number(restaurant.maxItemQuantity))
        ? Math.round(Number(restaurant.maxItemQuantity))
        : undefined,
  maxItemQuantity:
    Number.isFinite(Number(restaurant.maxItemQuantity))
      ? Math.round(Number(restaurant.maxItemQuantity))
      : Number.isFinite(Number(restaurant.max_item_quantity))
        ? Math.round(Number(restaurant.max_item_quantity))
        : undefined,
  max_headcount:
    Number.isFinite(Number(restaurant.max_headcount))
      ? Math.round(Number(restaurant.max_headcount))
      : Number.isFinite(Number(restaurant.maxHeadcount))
        ? Math.round(Number(restaurant.maxHeadcount))
        : undefined,
  maxHeadcount:
    Number.isFinite(Number(restaurant.maxHeadcount))
      ? Math.round(Number(restaurant.maxHeadcount))
      : Number.isFinite(Number(restaurant.max_headcount))
        ? Math.round(Number(restaurant.max_headcount))
        : undefined,
  status: restaurant.agentOrderingEnabled !== false ? 'open' : 'closed',
  isFavorite: false,
  popularityScore: 0,
  // "$".."$$$$" (FilterDrawer's price ids). Marketplace (DoorDash) rows carry
  // it when a price-tier search returned the store; unknown stays null, which
  // an active price filter excludes.
  priceRange: phantomPriceRange(restaurant),
  // Several tiers searched at once (capabilities.price_filter 'search'): the
  // set the store's tier is one of. A price filter holding all of them keeps
  // the row (useRestaurantFilters).
  priceRanges: phantomPriceRanges(restaurant),
  features: [
    restaurant.supports_catering ? 'catering' : null,
    ...(Array.isArray(restaurant.fulfillmentTypesSupported) && restaurant.fulfillmentTypesSupported.includes('catering')
      ? ['catering']
      : []),
  ].filter(Boolean),
  // Which services this restaurant can actually fulfil. Phantom strips
  // 'delivery' from this list for stores that don't run their own drivers, so
  // the card and the fulfillment bar can offer pickup only instead of letting
  // a coach shop in delivery mode and get refused at checkout.
  fulfillmentTypesSupported: Array.isArray(restaurant.fulfillmentTypesSupported)
    ? restaurant.fulfillmentTypesSupported.map((type) => String(type || '').trim().toLowerCase()).filter(Boolean)
    : Array.isArray(restaurant.fulfillment_types_supported)
      ? restaurant.fulfillment_types_supported.map((type) => String(type || '').trim().toLowerCase()).filter(Boolean)
      : [],
  selfDeliversEnabled: restaurant.selfDeliversEnabled ?? restaurant.self_delivers_enabled ?? null,
  _avgPrice: null,
  _items: [],
  _address: restaurant.address || restaurant.location || '',
  _coords:
    restaurant.coordinates?.lat != null && restaurant.coordinates?.lng != null
      ? restaurant.coordinates
      : Array.isArray(restaurant.coordinates) && restaurant.coordinates.length === 2
        ? { lat: Number(restaurant.coordinates[0]), lng: Number(restaurant.coordinates[1]) }
        : null,
  business_hours: restaurant.business_hours || null,
  timezone: restaurant.timezone || null,
  hours: normalizeRestaurantHours(restaurant.business_hours || null),
  // Location opening hours from Phantom ([{day,startTime,endTime}], sourced
  // from Deliverect). Empty = no known closed hours.
  openingHours: Array.isArray(restaurant.openingHours)
    ? restaurant.openingHours
    : Array.isArray(restaurant.opening_hours)
      ? restaurant.opening_hours
      : [],
  // Cached DeliverThat pickup-coverage tag from Phantom ('supported' |
  // 'unsupported' | 'unknown'). Advisory: 'unsupported' badges the card as
  // pickup-only; the checkout quote remains the deliverability authority.
  deliveryCoverage: restaurant.deliverThatCoverage || restaurant.delivery_coverage || 'unknown',
  // Kitchen prep time (minutes) from Phantom's ordering rules. Drives the
  // discovery ETA and how far out ASAP resolves. Missing/0 = not published,
  // and the ETA helpers substitute a default.
  prepTimeMinutes:
    Number.isFinite(Number(restaurant.prepTimeMinutes))
      ? Number(restaurant.prepTimeMinutes)
      : Number.isFinite(Number(restaurant.minimumLeadTimeMinutes))
        ? Number(restaurant.minimumLeadTimeMinutes)
        : null,
  // Required ordering notice from Phantom's rules — the validation gate,
  // distinct from prep since the split. Missing = none published.
  minimumLeadTimeMinutes:
    Number.isFinite(Number(restaurant.minimumLeadTimeMinutes))
      ? Number(restaurant.minimumLeadTimeMinutes)
      : null,
  // The engaged courier's minimum notice. Deliberately preserves the
  // distinction between "no courier engaged" (explicit null -> no courier
  // floor) and "the server never said" (key absent -> conservative fallback),
  // so an older Phantom can't accidentally read as "no floor".
  ...(Object.prototype.hasOwnProperty.call(restaurant, 'deliveryMinLeadTimeMinutes')
    ? {
        // Number(null) is 0, so guard null explicitly — "no courier engaged"
        // must stay null, not become a zero-minute courier floor.
        deliveryMinLeadTimeMinutes:
          restaurant.deliveryMinLeadTimeMinutes != null &&
          Number.isFinite(Number(restaurant.deliveryMinLeadTimeMinutes))
            ? Number(restaurant.deliveryMinLeadTimeMinutes)
            : null,
      }
    : {}),
  deliveryCourier: restaurant.deliveryCourier ?? null,
  // Restaurant-published pickup→dropoff estimate (Deliverect's
  // averageDeliveryTime); null = unknown → the card's ETA range widens.
  deliveryTransitEstimateMinutes:
    restaurant.deliveryTransitEstimateMinutes != null &&
    Number.isFinite(Number(restaurant.deliveryTransitEstimateMinutes))
      ? Number(restaurant.deliveryTransitEstimateMinutes)
      : null,
  // Deliverect busy mode, now also on the discovery listing so a backed-up
  // kitchen lengthens the ETA on the card, not just on the detail page.
  busyState: restaurant.busyState ?? null,
  supported_providers: ['mcp'],
  provider_restaurant_ids: {
    ...(restaurant.providerRestaurantIds || {}),
    mcp: restaurant.providerRestaurantId || restaurant.id,
  },
  api_id: restaurant.providerRestaurantId || restaurant.id,
  square_connection_id: null,
  integration_type: 'mcp_server',
  provider_name: 'mcp',
  mcp_server_url: 'stdio://phantom',
  mcp_server_auth_type: restaurant.mcp_server_auth_type || 'api_key',
  mcp_server_status: restaurant.mcp_server_status || 'configured',
  mcp_capabilities: {
    tools: [
      'search_restaurants',
      'get_menu',
      'validate_order',
      'quote_order',
      'submit_order',
      'get_order_status',
    ],
  },
});

const normalizeComparableText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeComparableAddress = (value) =>
  normalizeComparableText(value)
    .replace(/\broad\b/g, 'rd')
    .replace(/\bstreet\b/g, 'st')
    .replace(/\bavenue\b/g, 'ave')
    .replace(/\bdrive\b/g, 'dr')
    .replace(/\bboulevard\b/g, 'blvd');

const buildComparableKey = (row = {}) => {
  const name = normalizeComparableText(row.name);
  const address = normalizeComparableAddress(row.address || row._address);
  if (!name) return null;
  return `${name}::${address}`;
};

// Blends the team's resolved go-to restaurants into the organic result list.
// Organic rows win a duplicate (their card data is freshest); injected copies
// carry an internal `_goToInjected` marker so a later area change can strip
// them before re-merging — nothing rendered reads the marker, so the cards
// stay indistinguishable from organic results. Injected rows weren't searched
// for, so they pass the active query through the client matcher; organic rows
// are the provider's own answer to it and are never re-filtered.
export const mergeGoToRows = (baseRows = [], goToRows = [], normalizedQuery = '') => {
  if (!Array.isArray(goToRows) || goToRows.length === 0) return baseRows;
  const present = new Set(baseRows.map((row) => String(row?.id)));
  const additions = filterRowsByQuery(goToRows, normalizedQuery)
    .filter((row) => row?.id != null && !present.has(String(row.id)))
    .map((row) => ({ ...row, _goToInjected: true }));
  return additions.length ? [...baseRows, ...additions] : baseRows;
};

// DoorDash's name search doesn't answer the same way twice: before stores
// open it often sends only the few open now, and "pizza", "pho" or
// "chipotle" came back empty while 212 New York Pizza and Pho Lyfe sat in the
// nearby list on screen (2026-10-08). The nearby list (five merged searches)
// keeps closed stores that take orders for later. So a typed search is
// filled in from the nearby list already loaded for the same area, service,
// radius and slot: rows that match the query and that the provider's answer
// lacks, after the provider's own. It reads only what this browser already
// holds, so it costs no provider call, and it runs on every answer: a short
// answer is often non-empty but still missing the closed stores. The
// `_nearbyFill` marker is internal; nothing rendered reads it.
export const fillFromNearbyRows = (providerRows = [], nearbyRows = [], normalizedQuery = '') => {
  if (!normalizedQuery || !Array.isArray(nearbyRows) || nearbyRows.length === 0) return providerRows;
  const presentIds = new Set(providerRows.map((row) => String(row?.id)));
  const presentKeys = new Set(providerRows.map(buildComparableKey).filter(Boolean));
  const additions = filterRowsByQuery(nearbyRows, normalizedQuery)
    .filter((row) => row?.id != null && !row._goToInjected && !presentIds.has(String(row.id)))
    .filter((row) => !presentKeys.has(buildComparableKey(row)))
    .map((row) => ({ ...row, _nearbyFill: true }));
  return additions.length ? [...providerRows, ...additions] : providerRows;
};

// How old a nearby list may be to fill in a search: it supplies stores, and
// stores near an address don't change by the minute the way ETAs do.
const NEARBY_FILL_MAX_AGE_MS = 30 * 60 * 1000;

// The identity the go-to/saved/visited layer is resolved for: team + delivery
// origin (coords rounded like the request keys, address text). Rows resolved
// for one area must never be merged into another area's results, and a
// change of area drops the previous area's rows on the spot.
export const buildGoToAreaKey = (teamId, coords, address) =>
  [
    String(teamId || ''),
    coords?.lat != null && coords?.lng != null
      ? `${Number(coords.lat).toFixed(4)},${Number(coords.lng).toFixed(4)}`
      : '',
    String(address || '').trim().toLowerCase(),
  ].join('::');

const sortRows = (rows = []) =>
  [...rows].sort((left, right) => {
    if (Number.isFinite(left?._distanceMeters) && Number.isFinite(right?._distanceMeters)) {
      return left._distanceMeters - right._distanceMeters;
    }
    if (Number.isFinite(left?._distanceMeters)) return -1;
    if (Number.isFinite(right?._distanceMeters)) return 1;
    return left.name.localeCompare(right.name);
  });

// Discovery's date/time are the wall clock at the delivery address (no
// restaurant is picked yet — useDiscoveryFulfillment reads them the same way),
// so the instant sent to search is anchored there, not on the device clock.
export const buildRequestedTime = (fulfillment) => {
  if (!fulfillment?.date || !fulfillment?.time) return '';
  return wallClockToIso(fulfillment.date, fulfillment.time, coordsToTimeZone(fulfillment.coords)) || '';
};

// The time part of the search identity. ASAP is one identity per day:
// FulfillmentBar steps an ASAP slot forward every 5 minutes while the page
// sits open, and each step used to be a new search. Phantom's DoorDash browse
// cache ignores the time, so an idle ASAP tab cost 5 DoorDash calls every 10
// minutes, around the clock (2026-09-23). The request still carries the
// current slot; only a move that isn't an ASAP roll re-searches. A scheduled
// slot keeps its exact wall clock.
export const buildSearchSlotKey = (fulfillment) => {
  const date = fulfillment?.date || '';
  if (fulfillment?.asap === true && date) return `asap:${date}`;
  return date || fulfillment?.time ? `${date}T${fulfillment?.time || ''}` : '';
};

// One provider search per search identity: fetch DoorDash's server-side max
// (100 stores per query) up front and let the grid reveal rows progressively
// on scroll. dd-cli has no page/offset flag, and its rate limit is (as far as
// we know) metered per call, so one 100-store call beats re-querying with
// grown limits. The go-to lookup asks for the nearby list with this same
// limit (NEARBY_SEARCH_LIMIT in server/mcp/goToResolution.mjs), so Phantom
// answers both from one DoorDash call while it is in flight.
const DISCOVERY_FETCH_LIMIT = 100;

// "$".."$$$$" picks as the provider's price tiers ("1,2"), or '' for none.
export const priceTiersKeyOf = (priceRange) =>
  [...new Set((Array.isArray(priceRange) ? priceRange : []).map((range) => String(range ?? '').trim()))]
    .filter((range) => /^\${1,4}$/.test(range))
    .map((range) => range.length)
    .sort((a, b) => a - b)
    .join(',');

// The organic search's identity: what it was asked, never when.
export const buildOrganicRequestKey = ({ fulfillmentType = '', query = '', address = '', coords = null, radiusMiles, slotKey = '', priceTiers = '' } = {}) =>
  [
    fulfillmentType,
    query || '',
    String(address || '').toLowerCase(),
    coords?.lat != null && coords?.lng != null
      ? `${Number(coords.lat).toFixed(4)},${Number(coords.lng).toFixed(4)}`
      : '',
    Number.isFinite(Number(radiusMiles)) ? String(Math.max(1, Number(radiusMiles))) : '',
    slotKey,
    ...(priceTiers ? [`tiers:${priceTiers}`] : []),
  ].join('::');

// The searched-for origin: the map's center when set, else the address's.
const searchCoordsOf = (centerCoords, fulfillment) =>
  centerCoords?.lat != null && centerCoords?.lng != null
    ? centerCoords
    : fulfillment?.coords?.lat != null && fulfillment?.coords?.lng != null
      ? fulfillment.coords
      : null;

// The tiers "Show more restaurants" searches one by one when no price is
// picked: price tier is a real DoorDash filter every store sits in, so one
// search per tier lists stores the single nearby search left out (21 stores
// became 65 at a Sunnyvale address, 2026-09-19).
const MORE_NEARBY_TIERS = [1, 2, 3, 4];

const MORE_NEARBY_IDLE = Object.freeze({ identity: null, status: 'idle', tiersDone: [], added: 0, retryAt: null });

/**
 * Folds one tier search's rows into the list: a store already listed gets the
 * tier the search names (its own price, where it had none or only a set),
 * and a new store goes on the end. Same array when nothing changed.
 */
export const mergeMoreNearbyRows = (baseRows = [], moreRows = []) => {
  if (!Array.isArray(moreRows) || moreRows.length === 0) return baseRows;
  const pending = new Map();
  for (const row of moreRows) {
    if (row?.id != null && !pending.has(String(row.id))) pending.set(String(row.id), row);
  }
  let changed = false;
  const merged = baseRows.map((row) => {
    const more = pending.get(String(row?.id));
    if (!more) return row;
    pending.delete(String(row.id));
    if (!more.priceRange || row.priceRange === more.priceRange) return row;
    changed = true;
    return { ...row, priceRange: more.priceRange, priceRanges: null };
  });
  if (pending.size === 0 && !changed) return baseRows;
  return [...merged, ...pending.values()];
};

// GET /api/mcp/restaurants. A refusal throws with the payload (rate limits
// carry retryAfterSeconds; see describeSearchFailure). authorizedFetch, not
// bare fetch: discovery search proxies to Phantom on the platform's own agent
// key and is signed-in-only, so the bearer token has to ride along (the
// sibling go-to request already does this).
const requestRestaurantSearch = (params) =>
  authorizedFetch(`/api/mcp/restaurants${params.toString() ? `?${params.toString()}` : ''}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  }).then(async (response) => {
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const failure = new Error(payload?.error || payload?.message || 'Failed to load restaurants');
      failure.status = response.status;
      failure.payload = payload;
      throw failure;
    }
    return payload;
  });

export default function useRestaurantsSource({ fulfillment, centerCoords, radiusMiles, searchQuery, selectedService, priceRange } = {}) {
  const [rows, setRows] = useState([]);
  // The query the organic rows answer. A query change keeps the previous
  // rows on screen while the new search runs; the grid needs to know they
  // answer another query (useRestaurantFilters filters or hides them).
  const [rowsQuery, setRowsQuery] = useState('');
  const [err, setErr] = useState('');
  // The query `err` answers. A failed search leaves rowsQuery on the last
  // query that loaded, so without this the grid read a failed new query as
  // a search still to come and held its skeleton over the error.
  const [errQuery, setErrQuery] = useState('');
  // When a failed search runs again by itself (ms), or null. Rows already on
  // screen stay there meanwhile; the grid shows `err` above them.
  const [retryAt, setRetryAt] = useState(null);
  // Time the current search identity has spent waiting out rate limits, and
  // the timer that will search again.
  const retryBudgetRef = useRef({ identity: null, waitedMs: 0 });
  const retryTimerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  // Whether the nearby list carries prices: true / false, or null until one
  // has loaded. DoorDash rows have a price only when a per-tier search found
  // them, and a coach's own account lists nearby stores with one untiered
  // search, so its list has none (see priceFilterPauseReason).
  const [rowsCarryPrice, setRowsCarryPrice] = useState(null);
  const [capabilities, setCapabilities] = useState(latestCapabilities);
  const { activeTeam, loadingTeams } = useAuth();
  // A supply whose nearby list carries no price (capabilities.price_filter
  // 'search', a coach's own DoorDash account) filters by price with a tier
  // search: the picks ride the request, one provider call per change. On any
  // other supply the rows carry their price and the grid filters them, so
  // the picks never reach the request (the shared account's five-search
  // nearby list is the same with or without a price picked).
  const priceSearch = capabilities.price_filter === 'search';
  const priceTiersKey = priceSearch ? priceTiersKeyOf(priceRange) : '';

  // The team's go-to restaurants resolved for the current delivery area.
  // Fetched concurrently with the organic search; merged into `rows` from
  // whichever side lands later. Ref (not just state) so the organic fetch's
  // setRows sees the latest without re-running its effect. The rows are
  // tagged with the area they were resolved for; `goToRowsFor` hands them
  // out only for that same area, so an organic result landing right after an
  // address switch can't pick up the previous address's go-tos.
  const goToRef = useRef({ rows: [], areaKey: null });
  const goToRowsFor = (areaKey) => (goToRef.current.areaKey === areaKey ? goToRef.current.rows : []);
  // The delivery origin (coords + address, no team) the organic effect last
  // ran for. A different origin drops the rows immediately (see the effect)
  // instead of leaving the previous address's restaurants on screen until
  // the new search lands. The team is left out: the organic list is the same
  // for every team, and on a full page load the team arrives a moment after
  // the first search started, which is not a new area.
  const lastOriginKeyRef = useRef(null);
  const activeTeamIdRef = useRef(activeTeam?.id || null);
  activeTeamIdRef.current = activeTeam?.id || null;
  const [goToIds, setGoToIds] = useState(() => new Set());
  // Store ids resolved from restaurant-tagged saved locations — surfaced in
  // the grid's Saved section (not pinned like go-tos).
  const [savedResolvedIds, setSavedResolvedIds] = useState(() => new Set());
  // Store ids the team has actually ordered from at THIS delivery origin
  // (same saved location / delivery address, or same pickup search address).
  // Always injected into the grid even when the organic search omits them.
  const [visitedResolvedIds, setVisitedResolvedIds] = useState(() => new Set());
  // True while the go-to resolution for the current area is in flight, capped
  // at GO_TO_HOLD_MS. The grid holds its first paint on this so the team's
  // go-to restaurants are already on top when results appear, instead of
  // popping in above a list the coach is scanning. The cap means a slow cold
  // resolution degrades to today's behavior (rows splice in when ready).
  const [goToPending, setGoToPending] = useState(false);
  const searchQueryRef = useRef('');
  searchQueryRef.current = searchQueryKey(searchQuery);

  // The organic search re-runs on its identity, not on every slot move (see
  // buildSearchSlotKey). The requested instant is read from here when the
  // request goes out, so an ASAP search still sends the current slot.
  const fulfillmentRef = useRef(fulfillment);
  fulfillmentRef.current = fulfillment;
  const searchSlotKey = buildSearchSlotKey(fulfillment);
  // The organic search as it stands now (see buildOrganicRequestKey).
  const organicIdentity = buildOrganicRequestKey({
    fulfillmentType: (selectedService || '').trim().toLowerCase(),
    query: searchQueryKey(searchQuery),
    address: String(fulfillment?.address || '').trim(),
    coords: searchCoordsOf(centerCoords, fulfillment),
    radiusMiles,
    slotKey: searchSlotKey,
    priceTiers: priceTiersKey,
  });
  const organicIdentityRef = useRef(organicIdentity);
  organicIdentityRef.current = organicIdentity;
  // The current organic search's request (params without the slot's instant
  // or a price) and snapshot-cache identity, for "Show more restaurants".
  const organicRequestRef = useRef(null);
  // "Show more restaurants": the wider nearby list, searched one price tier
  // at a time and only when the coach asks (see expandNearby). The rows it
  // found stay merged into every refresh of the same search and go with it.
  const [moreNearby, setMoreNearby] = useState(MORE_NEARBY_IDLE);
  const moreNearbyRowsRef = useRef({ identity: null, rows: [] });
  const moreNearbyRunningRef = useRef(false);
  const withMoreNearby = (list, identity) =>
    moreNearbyRowsRef.current.identity === identity ? mergeMoreNearbyRows(list, moreNearbyRowsRef.current.rows) : list;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // When the painted organic rows came back from the network. Null until the
  // first search lands; that first search runs even in a background tab.
  const lastFetchedAtRef = useRef(null);
  // An identity change that arrived while the tab was hidden and still owes
  // a search. Bumping refreshTick re-runs the organic effect.
  const deferredWhileHiddenRef = useRef(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisibilityChange = () => {
      if (document.hidden) return;
      const fetchedAt = lastFetchedAtRef.current;
      const stale = fetchedAt != null && Date.now() - fetchedAt >= RETURN_REFRESH_STALE_MS;
      if (deferredWhileHiddenRef.current || stale) {
        deferredWhileHiddenRef.current = false;
        setRefreshTick((tick) => tick + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const normalizedQuery = searchQueryKey(searchQuery);
    const fulfillmentType = (selectedService || '').trim().toLowerCase();
    const address = String(fulfillment?.address || '').trim();

    // No address: nothing to search, so the previous address's results go
    // now, even in a background tab (clearing costs no provider call). A
    // team switch to a team with no address used to leave the old hotel's
    // restaurants on screen in a hidden tab. A cooldown countdown for the
    // old address goes with them (the effect's cleanup already cleared its
    // timer).
    if (fulfillmentType && !address) {
      deferredWhileHiddenRef.current = false;
      lastOriginKeyRef.current = buildGoToAreaKey('', null, '');
      setRows([]);
      setRowsQuery(normalizedQuery);
      setErr(NEEDS_ADDRESS_MESSAGE);
      setErrQuery(normalizedQuery);
      setRetryAt(null);
      setLoading(false);
      return undefined;
    }

    // A background tab doesn't re-search: nobody is looking, and each
    // DoorDash nearby list costs provider calls. The change is picked up when
    // the tab is shown again (the visibility listener above).
    if (lastFetchedAtRef.current != null && isDocumentHidden()) {
      deferredWhileHiddenRef.current = true;
      return undefined;
    }
    deferredWhileHiddenRef.current = false;

    const coords = searchCoordsOf(centerCoords, fulfillment);
    const requestKey = buildOrganicRequestKey({
      fulfillmentType,
      query: normalizedQuery,
      address,
      coords,
      radiusMiles,
      slotKey: searchSlotKey,
      priceTiers: priceTiersKey,
    });
    // The request, less the slot's instant (read when it goes out) and the
    // price (added below; "Show more restaurants" sends one tier at a time).
    const baseParams = new URLSearchParams();
    if (normalizedQuery) baseParams.set('query', normalizedQuery);
    if (fulfillmentType) baseParams.set('fulfillmentType', fulfillmentType);
    if (address) baseParams.set('address', address);
    if (coords?.lat != null && coords?.lng != null) {
      baseParams.set('lat', String(coords.lat));
      baseParams.set('lng', String(coords.lng));
    }
    if (Number.isFinite(Number(radiusMiles))) {
      baseParams.set('radiusMiles', String(Math.max(1, Number(radiusMiles))));
    }
    baseParams.set('limit', String(DISCOVERY_FETCH_LIMIT));
    const cacheIdentity = { fulfillmentType, address, coords, radiusMiles, requestedTime: searchSlotKey };
    organicRequestRef.current = { identity: requestKey, params: baseParams.toString(), cache: cacheIdentity };
    // Another search: the stores "Show more restaurants" found answered the
    // last one, and go. A refresh of the same search keeps them.
    if (moreNearbyRowsRef.current.identity !== requestKey) {
      moreNearbyRowsRef.current = { identity: null, rows: [] };
      setMoreNearby((prev) => (prev.identity == null ? prev : MORE_NEARBY_IDLE));
    }

    (async () => {
      const areaKey = buildGoToAreaKey(activeTeamIdRef.current, coords, address);
      const originKey = buildGoToAreaKey('', coords, address);
      const areaChanged = lastOriginKeyRef.current !== null && lastOriginKeyRef.current !== originKey;
      lastOriginKeyRef.current = originKey;

      // Keyed on the search identity's slot, not the exact instant, so an
      // ASAP entry still serves after the slot has rolled.
      const cachedRows = readPhantomRestaurantCache({
        query: normalizedQuery,
        ...cacheIdentity,
        priceTiers: priceTiersKey,
      });
      // The nearby list for this same identity, to fill in a typed search
      // (see fillFromNearbyRows). Read when the answer lands, so a nearby
      // list that lands meanwhile still counts.
      const withNearbyFill = (providerRows) =>
        normalizedQuery
          ? fillFromNearbyRows(
              providerRows,
              readPhantomRestaurantCache({
                query: '',
                ...cacheIdentity,
                // The nearby list under the same price pick: its rows answer
                // the same tiers.
                priceTiers: priceTiersKey,
                maxAgeMs: NEARBY_FILL_MAX_AGE_MS,
              }),
              normalizedQuery,
            )
          : providerRows;

      if (cachedRows?.length && !cancelled) {
        setRows(withMoreNearby(mergeGoToRows(withNearbyFill(cachedRows), goToRowsFor(areaKey), normalizedQuery), requestKey));
        setRowsQuery(normalizedQuery);
        setHasLoadedOnce(true);
      } else if (areaChanged && !cancelled) {
        // A new delivery origin with nothing cached for it: the previous
        // origin's restaurants must not stay on screen (badged as that
        // origin's visited places, sorted by that origin's distances) for the
        // seconds the new search takes. The grid shows its skeleton until the
        // new area's go-to/visited rows land, then those, then the organic
        // results. A query/time/service change within the same area keeps
        // the current rows under the "Updating…" pill as before.
        setRows((prev) => (prev.length ? [] : prev));
      }

      setLoading(!cachedRows?.length);
      setErr('');
      setRetryAt(null);
      try {
        await new Promise((resolve) => setTimeout(resolve, FETCH_COALESCE_MS));
        if (cancelled) return;

        const requestedTime = buildRequestedTime(fulfillmentRef.current);
        const params = new URLSearchParams(baseParams);
        if (requestedTime) {
          params.set('requestedTime', requestedTime);
        }
        // A price pick on a supply that filters by price with a search.
        if (priceTiersKey) {
          params.set('priceTiers', priceTiersKey);
        }

        const existingRequest = requestCache.get(requestKey);
        const now = Date.now();

        let payloadPromise;
        // 30s: dd-cli searches are rate-limited per user, so identical requests
        // (keystroke churn, remounts) reuse the in-flight/recent promise. The
        // session cache above already serves sub-minute revisits.
        if (existingRequest && (now - existingRequest.timestamp) < 30_000) {
          payloadPromise = existingRequest.promise;
        } else {
          payloadPromise = requestRestaurantSearch(params).finally(() => {
            const latest = requestCache.get(requestKey);
            if (latest?.promise === payloadPromise) {
              requestCache.delete(requestKey);
            }
          });

          requestCache.set(requestKey, {
            timestamp: now,
            promise: payloadPromise,
          });
        }

        const payload = await payloadPromise;

        // Provider capability declaration rides the envelope (absent on an
        // older Phantom -> platform defaults).
        const nextCapabilities = normalizeProviderCapabilities(payload?.capabilities);
        latestCapabilities = nextCapabilities;
        if (!cancelled) setCapabilities(nextCapabilities);

        const phantomRows = sortRows(
          (Array.isArray(payload?.restaurants) ? payload.restaurants : []).map(normalizePhantomRestaurant)
        );
        writePhantomRestaurantCache(phantomRows, {
          query: normalizedQuery,
          ...cacheIdentity,
          priceTiers: priceTiersKey,
        });

        // A superseded request (the coach moved on to another address or
        // query while this one was in flight) still warms the cache above,
        // but must not paint its rows under the current identity: two
        // switches in a row used to show whichever search finished LAST.
        if (cancelled) return;
        // The go-tos are looked up for the team as it is NOW, not as it was
        // when the search started: on a full page load the team arrives
        // while the search is in flight, and the go-to rows (filed under the
        // team) used to be dropped from the grid when the search landed.
        const landingAreaKey = buildGoToAreaKey(activeTeamIdRef.current, coords, address);
        setRows(withMoreNearby(mergeGoToRows(withNearbyFill(phantomRows), goToRowsFor(landingAreaKey), normalizedQuery), requestKey));
        setRowsQuery(normalizedQuery);
        setHasLoadedOnce(true);
        lastFetchedAtRef.current = Date.now();
        retryBudgetRef.current = { identity: null, waitedMs: 0 };
      } catch (e) {
        if (!cancelled) {
          const failure = describeSearchFailure(e);
          if (failure.retryAfterSeconds == null) console.warn('[discovery] search failed', e?.message || e);
          setErr(failure.message);
          setErrQuery(normalizedQuery);
          // Rows already on screen stay: a failed refresh (a tab back after
          // 15 minutes, a radius move) during a cooldown used to replace a
          // valid list with the error. An area change emptied them above.
          // Rate limited: search again once the window is over — never
          // earlier, and not while nobody is looking — within a budget; then
          // the grid's Try again is the coach's.
          if (failure.retryAfterSeconds != null) {
            const waitMs = providerReadRetryWaitMs(failure.retryAfterSeconds);
            const spent = retryBudgetRef.current.identity === requestKey ? retryBudgetRef.current.waitedMs : 0;
            if (spent + waitMs <= PROVIDER_READ_WAIT_BUDGET_MS) {
              retryBudgetRef.current = { identity: requestKey, waitedMs: spent + waitMs };
              setRetryAt(Date.now() + waitMs);
              retryTimerRef.current = setTimeout(() => {
                retryTimerRef.current = null;
                if (isDocumentHidden()) {
                  deferredWhileHiddenRef.current = true;
                  return;
                }
                setRefreshTick((tick) => tick + 1);
              }, waitMs);
            }
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [
    centerCoords?.lat,
    centerCoords?.lng,
    fulfillment?.address,
    fulfillment?.coords?.lat,
    fulfillment?.coords?.lng,
    searchSlotKey,
    radiusMiles,
    searchQuery,
    selectedService,
    priceTiersKey,
    refreshTick,
  ]);

  useEffect(() => {
    let cancelled = false;
    const teamId = activeTeam?.id || null;
    const address = String(fulfillment?.address || '').trim();
    const coords =
      centerCoords?.lat != null && centerCoords?.lng != null
        ? centerCoords
        : fulfillment?.coords?.lat != null && fulfillment?.coords?.lng != null
          ? fulfillment.coords
          : null;

    const areaKey = buildGoToAreaKey(teamId, coords, address);

    const clear = (nextAreaKey = null) => {
      goToRef.current = { rows: [], areaKey: nextAreaKey };
      setGoToIds((prev) => (prev.size === 0 ? prev : new Set()));
      setSavedResolvedIds((prev) => (prev.size === 0 ? prev : new Set()));
      setVisitedResolvedIds((prev) => (prev.size === 0 ? prev : new Set()));
      setRows((prev) => (prev.some((row) => row._goToInjected) ? prev.filter((row) => !row._goToInjected) : prev));
    };

    if (!teamId && loadingTeams && (coords || address)) {
      // A full page load restores the address before the team is known.
      // Hold the grid as if the go-tos were resolving: painting cached cards
      // now and swapping them for the skeleton when the team lands read as
      // a flicker (cards, skeleton, cards).
      setGoToPending(true);
      const teamHoldTimer = setTimeout(() => setGoToPending(false), GO_TO_HOLD_MS);
      return () => clearTimeout(teamHoldTimer);
    }

    if (!teamId || (!coords && !address)) {
      clear();
      setGoToPending(false);
      return undefined;
    }

    // Another area: the previous area's go-to/saved/visited rows and id sets
    // are meaningless here — drop them now, not when the new response lands.
    // (The pinned/saved/visited badges would otherwise keep marking the old
    // area's stores while the new area resolves.)
    if (goToRef.current.areaKey !== areaKey) {
      clear(areaKey);
    }

    setGoToPending(true);
    const holdTimer = setTimeout(() => {
      setGoToPending(false);
    }, GO_TO_HOLD_MS);

    (async () => {
      try {
        const params = new URLSearchParams({ teamId });
        if (coords?.lat != null && coords?.lng != null) {
          params.set('lat', String(coords.lat));
          params.set('lng', String(coords.lng));
        }
        if (address) params.set('address', address);

        const requestKey = [
          teamId,
          coords?.lat != null ? `${Number(coords.lat).toFixed(4)},${Number(coords.lng).toFixed(4)}` : '',
          address.toLowerCase(),
        ].join('::');
        const existing = goToRequestCache.get(requestKey);
        const now = Date.now();
        let payloadPromise;
        if (existing && now - existing.timestamp < 30_000) {
          payloadPromise = existing.promise;
        } else {
          payloadPromise = authorizedFetch(`/api/mcp/go-to-restaurants?${params.toString()}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
          }).then(async (response) => {
            const payload = await response.json().catch(() => null);
            if (!response.ok) {
              throw new Error(payload?.error || 'Failed to load go-to restaurants');
            }
            return payload;
          });
          goToRequestCache.set(requestKey, { timestamp: now, promise: payloadPromise });
          payloadPromise.catch(() => {
            const latest = goToRequestCache.get(requestKey);
            if (latest?.promise === payloadPromise) goToRequestCache.delete(requestKey);
          });
        }

        const payload = await payloadPromise;
        if (cancelled) return;
        const rawRows = Array.isArray(payload?.restaurants) ? payload.restaurants : [];
        const normalized = rawRows.map(normalizePhantomRestaurant);
        // pinnedIds/savedIds carry the provider's raw store ids, but
        // normalization rewrites row ids (to the Supabase shadow-row id when
        // one exists) — translate by pairing raw and normalized rows before
        // sorting. An older server without the id lists pins everything
        // (previous behavior).
        const pinnedRaw = new Set(
          (Array.isArray(payload?.pinnedIds) ? payload.pinnedIds : rawRows.map((row) => row?.id)).map(String)
        );
        const savedRaw = new Set((Array.isArray(payload?.savedIds) ? payload.savedIds : []).map(String));
        const visitedRaw = new Set((Array.isArray(payload?.visitedIds) ? payload.visitedIds : []).map(String));
        const pinned = new Set();
        const saved = new Set();
        const visited = new Set();
        normalized.forEach((row, index) => {
          const rawId = String(rawRows[index]?.id);
          if (pinnedRaw.has(rawId)) pinned.add(String(row.id));
          if (savedRaw.has(rawId)) saved.add(String(row.id));
          if (visitedRaw.has(rawId)) visited.add(String(row.id));
        });
        const goToRows = sortRows(normalized);
        // The detail page seeds from the shared snapshot cache and only shows
        // a DoorDash ASAP line when the row carries the provider's ETA quote —
        // the detail endpoint deliberately drops it. Organic rows are cached
        // by the search effect above; injected go-to/saved/visited rows would
        // otherwise never reach the cache, so a card showing "27 min" landed
        // on a page with no ETA. Own key (not the search key): the search
        // entry keeps its cache-hit semantics and this one shares the same
        // 60s TTL.
        writePhantomRestaurantCache(goToRows, {
          query: `go-to:${teamId}`,
          address,
          coords,
        });
        goToRef.current = { rows: goToRows, areaKey };
        setGoToIds(pinned);
        setSavedResolvedIds(saved);
        setVisitedResolvedIds(visited);
        setRows((prev) =>
          mergeGoToRows(
            prev.filter((row) => !row._goToInjected),
            goToRows,
            searchQueryRef.current
          )
        );
      } catch {
        // Go-to resolution is a best-effort boost; a failure just means the
        // organic list renders alone. Never surface an error for it.
        if (!cancelled) clear(areaKey);
      } finally {
        clearTimeout(holdTimer);
        if (!cancelled) setGoToPending(false);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(holdTimer);
    };
  }, [
    activeTeam?.id,
    loadingTeams,
    centerCoords?.lat,
    centerCoords?.lng,
    fulfillment?.address,
    fulfillment?.coords?.lat,
    fulfillment?.coords?.lng,
  ]);

  // Read from the nearby list only: a typed search's rows never carry a
  // price, and injected go-to/saved/visited rows weren't priced by it.
  useEffect(() => {
    if (!hasLoadedOnce || rowsQuery) return;
    const organic = rows.filter((row) => !row._goToInjected);
    if (organic.length === 0) return;
    const next = organic.some((row) => Boolean(row.priceRange));
    setRowsCarryPrice((prev) => (prev === next ? prev : next));
  }, [rows, rowsQuery, hasLoadedOnce]);

  // The grid's Try again: the same search once more, with a fresh budget.
  const retry = useCallback(() => {
    retryBudgetRef.current = { identity: null, waitedMs: 0 };
    setRefreshTick((tick) => tick + 1);
  }, []);

  // "Show more restaurants" on a supply whose nearby list is one search
  // (price_filter 'search'): the per-tier searches the shared account's list
  // runs on every load, here only when the coach asks. The nearby list only,
  // never a typed search. With a price picked, the picked tiers one by one;
  // with one tier picked the list already is that tier's search.
  const moreNearbyTiers = (() => {
    if (!priceSearch || searchQueryKey(searchQuery)) return [];
    const tiers = priceTiersKey ? priceTiersKey.split(',').map(Number) : MORE_NEARBY_TIERS;
    return tiers.length > 1 ? tiers : [];
  })();
  const moreNearbyTiersRef = useRef(moreNearbyTiers);
  moreNearbyTiersRef.current = moreNearbyTiers;

  // One tier search at a time, never in parallel, and the first rate limit
  // ends the run (each later search would be refused into the same window
  // and only push it out). The tiers still to search wait for the next
  // click. The searches go through the same route as the nearby list, one
  // DoorDash call each.
  const expandNearby = useCallback(async () => {
    const identity = organicIdentityRef.current;
    const request = organicRequestRef.current;
    if (moreNearbyRunningRef.current || !request || request.identity !== identity) return;
    const doneBefore = moreNearbyRowsRef.current.identity === identity ? moreNearbyRowsRef.current.tiersDone ?? [] : [];
    const tiers = moreNearbyTiersRef.current.filter((tier) => !doneBefore.includes(tier));
    if (tiers.length === 0) return;
    moreNearbyRunningRef.current = true;
    if (moreNearbyRowsRef.current.identity !== identity) {
      moreNearbyRowsRef.current = { identity, rows: [], tiersDone: [] };
    }
    setMoreNearby((prev) => ({
      ...(prev.identity === identity ? prev : { ...MORE_NEARBY_IDLE, identity }),
      status: 'running',
      retryAt: null,
    }));
    const present = new Set(rowsRef.current.map((row) => String(row?.id)));
    let outcome = { status: 'done', retryAt: null };
    try {
      for (const tier of tiers) {
        const params = new URLSearchParams(request.params);
        const requestedTime = buildRequestedTime(fulfillmentRef.current);
        if (requestedTime) params.set('requestedTime', requestedTime);
        params.set('priceTiers', String(tier));
        let payload;
        try {
          payload = await requestRestaurantSearch(params);
        } catch (error) {
          const failure = describeSearchFailure(error);
          if (failure.retryAfterSeconds == null) console.warn('[discovery] more restaurants failed', error?.message || error);
          outcome = failure.retryAfterSeconds != null
            ? { status: 'rate_limited', retryAt: Date.now() + providerReadRetryWaitMs(failure.retryAfterSeconds) }
            : { status: 'failed', retryAt: null };
          break;
        }
        // The coach moved on (another address, price, service…): this
        // answer is not about the list on screen.
        if (organicIdentityRef.current !== identity) return;
        const found = sortRows(
          (Array.isArray(payload?.restaurants) ? payload.restaurants : []).map(normalizePhantomRestaurant)
        );
        // The store page seeds from the snapshot cache, like every card.
        writePhantomRestaurantCache(found, { query: '', ...request.cache, priceTiers: String(tier) });
        const added = found.filter((row) => row?.id != null && !present.has(String(row.id)));
        added.forEach((row) => present.add(String(row.id)));
        const kept = moreNearbyRowsRef.current;
        moreNearbyRowsRef.current = {
          identity,
          rows: mergeMoreNearbyRows(kept.rows, found),
          tiersDone: [...(kept.tiersDone ?? []), tier],
        };
        setRows((prev) => mergeMoreNearbyRows(prev, found));
        setMoreNearby((prev) =>
          prev.identity === identity
            ? { ...prev, tiersDone: [...prev.tiersDone, tier], added: prev.added + added.length }
            : prev
        );
      }
    } finally {
      moreNearbyRunningRef.current = false;
    }
    if (organicIdentityRef.current !== identity) return;
    setMoreNearby((prev) => (prev.identity === identity ? { ...prev, ...outcome } : prev));
  }, []);

  const moreNearbyState = moreNearby.identity === organicIdentity ? moreNearby : MORE_NEARBY_IDLE;
  const moreNearbyRemaining = moreNearbyTiers.filter((tier) => !moreNearbyState.tiersDone.includes(tier)).length;
  const nearbyExpansion = {
    // Offered under a settled nearby list: loaded, not loading, not failed.
    available:
      moreNearbyTiers.length > 0 &&
      hasLoadedOnce &&
      !rowsQuery &&
      ((!loading && !err && moreNearbyRemaining > 0) || moreNearbyState.status !== 'idle'),
    status: moreNearbyState.status,
    added: moreNearbyState.added,
    retryAt: moreNearbyState.retryAt,
    remaining: moreNearbyRemaining,
    expand: expandNearby,
  };

  return { rows, rowsQuery, setRows, loading, err, errQuery, retryAt, retry, hasLoadedOnce, capabilities, goToIds, savedResolvedIds, visitedResolvedIds, goToPending, rowsCarryPrice, nearbyExpansion };
}
